import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  UsageSummaryData, ProjectUsageItem, getUsageSummary, getUsageByProject,
  PlatformReconciliationData, getPlatformReconciliation,
  refreshPlatformReconciliation, rebuildPlatformReconciliation,
  PlatformReconciliationVideo, PlatformReconciliationImage, PlatformReconciliationLLM,
  PlatformReconciliationService,
  PlatformReconciliationRangeData, getPlatformReconciliationByRange,
} from '../api';
import Breadcrumb from '../components/Breadcrumb';
import EmptyState from '../components/EmptyState';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { formatCreditsAsYuan } from '../utils/usageSummary';

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function formatNumber(n: number): string {
  return n.toLocaleString('zh-CN');
}

function formatCost(cost: number): string {
  if (cost === 0) return '-';
  if (cost < 0.01) return `¥${cost.toFixed(4)}`;
  return `¥${cost.toFixed(2)}`;
}

function formatDuration(seconds: number): string {
  if (seconds === 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m${s}s` : `${m}m`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

const PAGE_SIZE = 10;

// ==================== Tab: Overview ====================

const OverviewTab: React.FC = () => {
  const { t } = useTranslation('profile');
  const toast = useToast();
  const [summary, setSummary] = useState<UsageSummaryData | null>(null);
  const [items, setItems] = useState<ProjectUsageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projectIdInput, setProjectIdInput] = useState('');
  const [owner, setOwner] = useState('');
  const [ownerInput, setOwnerInput] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'total_cost'>('created_at');
  const [exporting, setExporting] = useState(false);
  const [copiedProjectId, setCopiedProjectId] = useState('');

  const copyProjectId = (id: string) => {
    navigator.clipboard?.writeText(id)
      .then(() => {
        setCopiedProjectId(id);
        window.setTimeout(() => setCopiedProjectId(''), 1500);
      })
      .catch(() => { /* 剪贴板不可用时静默 */ });
  };

  useEffect(() => {
    getUsageSummary().then(setSummary).catch((err: any) => toast.error(t('usageSummary.loadSummaryFailed', { error: err.message || err })));
  }, [t, toast]);

  const loadPage = useCallback((p: number) => {
    setListLoading(true);
    getUsageByProject(p, PAGE_SIZE, search || undefined, owner || undefined, false, projectId || undefined, sortBy)
      .then(res => {
        setItems(res.items || []);
        setTotal(res.total);
        setPage(p);
      })
      .catch((err: any) => toast.error(t('usageSummary.loadProjectFailed', { error: err.message || err })))
      .finally(() => {
        setListLoading(false);
        setLoading(false);
      });
  }, [search, owner, projectId, sortBy, t, toast]);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('usageSummary.loading')}</div>;
  }

  const totalCost = summary
    ? summary.llmCost + summary.imageCost + summary.videoCost + (summary.serviceCost || 0)
    : 0;

  return (
    <>
      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>LLM Token</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatTokens(summary.llmTotalTokens)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('usageSummary.llmCost')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(summary.llmCost)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('usageSummary.imageCount')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{formatNumber(summary.imageCount)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('usageSummary.imageCost')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(summary.imageCost)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('usageSummary.videoDuration')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>{formatDuration(summary.videoDuration)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>{t('usageSummary.videoCost')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(summary.videoCost)}</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1.25rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginBottom: '0.35rem' }}>服务成本</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--warning)' }}>{formatCost(summary.serviceCost || 0)}</div>
          </div>
        </div>
      )}

      {/* Total cost banner */}
      {summary && (
        <div className="card" style={{
          textAlign: 'center', padding: '1rem 1.25rem', marginBottom: '1.5rem',
          background: 'var(--primary-light)', borderColor: 'var(--primary)',
        }}>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-2)', marginRight: '0.75rem' }}>{t('usageSummary.totalCostLabel')}</span>
          <span style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--primary)' }}>{formatCost(totalCost)}</span>
        </div>
      )}

      {/* Project usage list */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('usageSummary.projectDetailTitle')}</h3>
          <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setProjectId(projectIdInput); setOwner(ownerInput); }} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder={t("usageSummary.searchProjectPlaceholder")}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{
                padding: '0.35rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px',
                border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', width: '12rem', outline: 'none',
              }}
            />
            <input
              type="text"
              placeholder={t("usageSummary.searchProjectIdPlaceholder")}
              value={projectIdInput}
              onChange={e => setProjectIdInput(e.target.value)}
              style={{
                padding: '0.35rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px',
                border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', width: '12rem', outline: 'none',
              }}
            />
            <input
              type="text"
              placeholder={t("usageSummary.searchOwnerPlaceholder")}
              value={ownerInput}
              onChange={e => setOwnerInput(e.target.value)}
              style={{
                padding: '0.35rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px',
                border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', width: '10rem', outline: 'none',
              }}
            />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as 'created_at' | 'total_cost')}
              style={{
                padding: '0.35rem 0.75rem', fontSize: '0.85rem', borderRadius: '6px',
                border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', width: '9rem', outline: 'none',
              }}
            >
              <option value="created_at">{t('usageSummary.sortByCreatedAt')}</option>
              <option value="total_cost">{t('usageSummary.sortByTotalCost')}</option>
            </select>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              style={{ fontSize: '0.8rem' }}
            >
              {t('usageSummary.searchBtn')}
            </button>
            {(search || projectId || owner || sortBy !== 'created_at') && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { setSearchInput(''); setSearch(''); setProjectIdInput(''); setProjectId(''); setOwnerInput(''); setOwner(''); setSortBy('created_at'); }}
                style={{ fontSize: '0.8rem' }}
              >
                {t('usageSummary.clearBtn')}
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  const all = await getUsageByProject(1, 10000, search || undefined, owner || undefined, true, projectId || undefined, sortBy);
                  const rows = all.items || [];
                  const esc = (v: unknown) => {
                    const s = String(v ?? '');
                    if (s.includes(',') || s.includes('"') || s.includes('\n') || /^[=+\-@]/.test(s)) {
                      return '"' + s.replace(/"/g, '""') + '"';
                    }
                    return s;
                  };
                  const header = t('usageSummary.csvHeaders').split(',');
                  const csv = '﻿' + header.join(',') + '\n' + rows.map(r =>
                    [esc(r.projectId), esc(r.projectName), esc(r.ownerName), r.llmTokens, r.llmCost, r.imageCount, r.imageCost, r.videoCount, r.videoDuration, r.videoCost, r.serviceCost || 0, r.totalCost, esc(formatCreditsAsYuan(r.totalCredits))].join(',')
                  ).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${t('usageSummary.csvFilename')}_${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err: any) {
                  toast.error(t('usageSummary.exportFailed', { error: err.message || err }));
                } finally {
                  setExporting(false);
                }
              }}
              style={{ fontSize: '0.8rem' }}
            >
              {exporting ? t('usageSummary.exporting') : t('usageSummary.exportCsv')}
            </button>
          </form>
        </div>
        {items.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={thStyle}>{t('usageSummary.thProject')}</th>
                    <th style={thStyle}>Owner</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>LLM Token</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thLlmCost')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thImageCount')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thImageCost')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thVideoCount')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thVideoDuration')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thVideoCost')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thServiceCost')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thTotalCost')}</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thTotalCostYuan')}</th>
                    <th style={thStyle}>{t('usageSummary.thCreatedAt')}</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>{t('usageSummary.thAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.projectId} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', color: 'var(--text-3)' }}>
                          <code style={{ fontFamily: 'monospace' }}>{row.projectId}</code>
                          <button
                            type="button"
                            onClick={() => copyProjectId(row.projectId)}
                            title={copiedProjectId === row.projectId ? t('usageSummary.projectIdCopied') : t('usageSummary.copyProjectId')}
                            aria-label={t('usageSummary.copyProjectId')}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                              color: copiedProjectId === row.projectId ? 'var(--success)' : 'var(--text-3)', lineHeight: 0,
                            }}
                          >
                            {copiedProjectId === row.projectId ? <CheckIcon /> : <CopyIcon />}
                          </button>
                        </div>
                        <Link to={`/projects/${row.projectId}`} title={row.projectName || row.projectId} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}>
                          {(row.projectName || row.projectId).length > 8 ? (row.projectName || row.projectId).slice(0, 8) + '...' : (row.projectName || row.projectId)}
                        </Link>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--text-2)' }}>{row.ownerName || '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(row.llmTokens)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.llmCost)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.imageCount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.imageCost)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(row.videoCount)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatDuration(row.videoDuration)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.videoCost)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(row.serviceCost || 0)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: 'var(--warning)' }}>{formatCost(row.totalCost)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 500 }}>{formatCreditsAsYuan(row.totalCredits)}</td>
                      <td style={{ ...tdStyle, fontSize: '0.78rem', color: 'var(--text-3)' }}>{row.createdAt}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <Link to={`/projects/${row.projectId}/usage`} className="btn btn-ghost btn-sm" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', textDecoration: 'none' }}>
                          {t('usageSummary.detailBtn')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem',
                borderTop: '1px solid var(--border)',
              }}>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page <= 1 || listLoading}
                  onClick={() => loadPage(page - 1)}
                >
                  {t('usageSummary.prevPage')}
                </button>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>
                  {page} / {totalPages}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page >= totalPages || listLoading}
                  onClick={() => loadPage(page + 1)}
                >
                  {t('usageSummary.nextPage')}
                </button>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon="&#128202;" description={t("usageSummary.emptyUsage")} />
        )}
      </div>
    </>
  );
};

// ==================== Tab: Reconciliation ====================

// Shared tables for monthly/range reconciliation: video/image/LLM + total card
interface ReconciliationTablesProps {
  videos: PlatformReconciliationVideo[];
  images: PlatformReconciliationImage[];
  llms: PlatformReconciliationLLM[];
  services: PlatformReconciliationService[];
  totalCost: number;
  totalLabel: string;       // e.g. "Monthly Total" / "Range Total"
  emptyDescription: string; // e.g. "No data for April 2026"
}

function groupByPlatform<T extends { platform?: string; provider?: string }>(
  items: T[],
  key: 'platform' | 'provider' = 'platform'
): { platform: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = (key === 'provider' ? (item as any).provider : (item as any).platform) || '';
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(item);
  }
  return Array.from(map.entries()).map(([platform, items]) => ({ platform, items }));
}

const ReconciliationTables: React.FC<ReconciliationTablesProps> = ({
  videos, images, llms, services, totalCost, totalLabel, emptyDescription,
}) => {
  const { t } = useTranslation('profile');
  const hasData = videos.length > 0 || images.length > 0 || llms.length > 0 || services.length > 0;
  if (!hasData) {
    return <EmptyState icon="&#128202;" description={emptyDescription} />;
  }

  const serviceSubtotal = services.reduce(
    (a, s) => ({ count: a.count + s.count, cost: a.cost + s.cost, credits: a.credits + s.credits }),
    { count: 0, cost: 0, credits: 0 },
  );

  const videoGroups = groupByPlatform(videos);
  const imageGroups = groupByPlatform(images);
  const llmGroups = groupByPlatform(llms, 'provider');

  const videoSubtotal = { count: 0, duration: 0, cost: 0, credits: 0 };
  videos.forEach(v => { videoSubtotal.count += v.count; videoSubtotal.duration += v.duration; videoSubtotal.cost += v.cost; videoSubtotal.credits += v.credits; });
  const imageSubtotal = { count: 0, cost: 0, credits: 0 };
  images.forEach(v => { imageSubtotal.count += v.count; imageSubtotal.cost += v.cost; imageSubtotal.credits += v.credits; });
  const llmSubtotal = { callCount: 0, inputTokens: 0, outputTokens: 0, cost: 0, credits: 0 };
  llms.forEach(v => { llmSubtotal.callCount += v.callCount; llmSubtotal.inputTokens += v.inputTokens; llmSubtotal.outputTokens += v.outputTokens; llmSubtotal.cost += v.cost; llmSubtotal.credits += v.credits; });

  const subtotalRowStyle: React.CSSProperties = { borderTop: '1px solid var(--border)', fontWeight: 600, background: 'rgba(99,102,241,0.08)', fontSize: '0.82rem' };

  return (
    <>
      {videos.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{t('usageSummary.videoGenerationTitle')}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('usageSummary.thPlatform')}</th>
                  <th style={thStyle}>{t('usageSummary.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thGenCount')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thDurationSec')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thCostYuan')}</th>
                </tr>
              </thead>
              <tbody>
                {videoGroups.map(g => {
                  const sub = { count: 0, duration: 0, cost: 0 };
                  g.items.forEach(v => { sub.count += v.count; sub.duration += v.duration; sub.cost += v.cost; });
                  return (
                    <React.Fragment key={g.platform}>
                      {g.items.map((v, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>{v.platform}</td>
                          <td style={tdStyle}>{v.model}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(v.count)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(v.duration)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(v.cost)}</td>
                        </tr>
                      ))}
                      {videoGroups.length > 1 && (
                        <tr style={subtotalRowStyle}>
                          <td style={tdStyle}>{t('usageSummary.platformSubtotal', { platform: g.platform })}</td>
                          <td style={tdStyle}></td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(sub.count)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(sub.duration)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(sub.cost)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                  <td style={tdStyle} colSpan={2}>{t('usageSummary.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(videoSubtotal.count)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(videoSubtotal.duration)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(videoSubtotal.cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{t('usageSummary.imageGenerationTitle')}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('usageSummary.thPlatform')}</th>
                  <th style={thStyle}>{t('usageSummary.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thGenCount')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thCostYuan')}</th>
                </tr>
              </thead>
              <tbody>
                {imageGroups.map(g => {
                  const sub = { count: 0, cost: 0 };
                  g.items.forEach(v => { sub.count += v.count; sub.cost += v.cost; });
                  return (
                    <React.Fragment key={g.platform}>
                      {g.items.map((v, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>{v.platform}</td>
                          <td style={tdStyle}>{v.model}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(v.count)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(v.cost)}</td>
                        </tr>
                      ))}
                      {imageGroups.length > 1 && (
                        <tr style={subtotalRowStyle}>
                          <td style={tdStyle}>{t('usageSummary.platformSubtotal', { platform: g.platform })}</td>
                          <td style={tdStyle}></td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(sub.count)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(sub.cost)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                  <td style={tdStyle} colSpan={2}>{t('usageSummary.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(imageSubtotal.count)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(imageSubtotal.cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {llms.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{t('usageSummary.llmTitle')}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('usageSummary.thProvider')}</th>
                  <th style={thStyle}>{t('usageSummary.thModel')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thCalls')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thInputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thOutputToken')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thCostYuan')}</th>
                </tr>
              </thead>
              <tbody>
                {llmGroups.map(g => {
                  const sub = { callCount: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
                  g.items.forEach(v => { sub.callCount += v.callCount; sub.inputTokens += v.inputTokens; sub.outputTokens += v.outputTokens; sub.cost += v.cost; });
                  return (
                    <React.Fragment key={g.platform}>
                      {g.items.map((v, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={tdStyle}>{v.provider}</td>
                          <td style={tdStyle}>{v.model}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(v.callCount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(v.inputTokens)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(v.outputTokens)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(v.cost)}</td>
                        </tr>
                      ))}
                      {llmGroups.length > 1 && (
                        <tr style={subtotalRowStyle}>
                          <td style={tdStyle}>{t('usageSummary.platformSubtotal', { platform: g.platform })}</td>
                          <td style={tdStyle}></td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(sub.callCount)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(sub.inputTokens)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(sub.outputTokens)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(sub.cost)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                  <td style={tdStyle} colSpan={2}>{t('usageSummary.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(llmSubtotal.callCount)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(llmSubtotal.inputTokens)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(llmSubtotal.outputTokens)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(llmSubtotal.cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {services.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>服务 / 外部分析</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>项目</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>次数</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('usageSummary.thCostYuan')}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={tdStyle}>{s.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(s.count)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(s.cost)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                  <td style={tdStyle}>{t('usageSummary.subtotal')}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatNumber(serviceSubtotal.count)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--warning)' }}>{formatCost(serviceSubtotal.cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{
        textAlign: 'center', padding: '1rem 1.25rem',
        background: 'var(--primary-light)', borderColor: 'var(--primary)',
      }}>
        <span style={{ fontSize: '0.9rem', color: 'var(--text-2)', marginRight: '0.75rem' }}>{totalLabel}</span>
        <span style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--primary)' }}>{formatCost(totalCost)}</span>
      </div>
    </>
  );
};

// Monthly view (month selector / refresh / rebuild)
interface MonthlyReconciliationViewProps { toast: ReturnType<typeof useToast>; }

const MonthlyReconciliationView: React.FC<MonthlyReconciliationViewProps> = ({ toast }) => {
  const { t } = useTranslation('profile');
  const [data, setData] = useState<PlatformReconciliationData | null>(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const load = useCallback((m?: string) => {
    setLoading(true);
    getPlatformReconciliation(m)
      .then(res => { setData(res); setMonth(res.month); })
      .catch((err: any) => toast.error(t('usageSummary.loadReconcileFailed', { error: err.message || err })))
      .finally(() => setLoading(false));
  }, [t, toast]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    refreshPlatformReconciliation()
      .then(() => { toast.success(t('usageSummary.refreshSuccess')); load(month); })
      .catch((err: any) => toast.error(t('usageSummary.refreshFailed', { error: err.message || err })))
      .finally(() => setRefreshing(false));
  };

  const handleRebuild = () => {
    if (!window.confirm(t('usageSummary.rebuildConfirm'))) return;
    setRebuilding(true);
    rebuildPlatformReconciliation()
      .then(() => { toast.success(t('usageSummary.rebuildSuccess')); load(month); })
      .catch((err: any) => toast.error(t('usageSummary.rebuildFailed', { error: err.message || err })))
      .finally(() => setRebuilding(false));
  };

  const goMonth = (delta: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setMonth(next);
    load(next);
  };

  const formatMonthLabel = (m: string) => {
    const [y, mo] = m.split('-');
    return t('usageSummary.monthLabel', { year: y, month: parseInt(mo) });
  };

  if (loading && !data) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('usageSummary.loading')}</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => goMonth(-1)} disabled={loading}>&lt;</button>
          <span style={{ fontSize: '1.1rem', fontWeight: 600, minWidth: '8rem', textAlign: 'center' }}>
            {month ? formatMonthLabel(month) : '-'}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => goMonth(1)} disabled={loading}>&gt;</button>
        </div>

        {data?.availableMonths && data.availableMonths.length > 0 && (
          <select
            value={month}
            onChange={e => { setMonth(e.target.value); load(e.target.value); }}
            style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text)' }}
          >
            {data.availableMonths.map(m => (
              <option key={m} value={m}>{formatMonthLabel(m)}</option>
            ))}
          </select>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={handleRefresh} disabled={refreshing || loading}>
            {refreshing ? t('usageSummary.refreshing') : t('usageSummary.refreshData')}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleRebuild} disabled={rebuilding || loading} style={{ color: 'var(--text-3)' }}>
            {rebuilding ? t('usageSummary.rebuilding') : t('usageSummary.rebuildAll')}
          </button>
        </div>
      </div>

      <ReconciliationTables
        videos={data?.video || []}
        images={data?.image || []}
        llms={data?.llm || []}
        services={data?.service || []}
        totalCost={data?.totalCost || 0}
        totalLabel={t('usageSummary.monthlyTotal')}
        emptyDescription={t('usageSummary.monthlyEmpty', { month: month ? formatMonthLabel(month) : '' })}
      />
    </>
  );
};

// Range view (direct query on raw usage table, no monthly aggregation)
interface RangeReconciliationViewProps { toast: ReturnType<typeof useToast>; }

// Default: last 7 days including today
function defaultRangeDates(): { start: string; end: string } {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const end = new Date(today);
  const start = new Date(today);
  start.setDate(start.getDate() - 6);
  return { start: fmt(start), end: fmt(end) };
}

const RangeReconciliationView: React.FC<RangeReconciliationViewProps> = ({ toast }) => {
  const { t } = useTranslation('profile');
  const [{ start, end }] = useState(defaultRangeDates);
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [data, setData] = useState<PlatformReconciliationRangeData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((s: string, e: string) => {
    setLoading(true);
    getPlatformReconciliationByRange(s, e)
      .then(res => setData(res))
      .catch((err: any) => toast.error(t('usageSummary.loadRangeFailed', { error: err.message || err })))
      .finally(() => setLoading(false));
  }, [t, toast]);

  useEffect(() => { load(start, end); }, [load, start, end]);

  const handleQuery = () => {
    if (!startDate || !endDate) {
      toast.error(t('usageSummary.selectDateRange'));
      return;
    }
    if (endDate < startDate) {
      toast.error(t('usageSummary.endBeforeStart'));
      return;
    }
    load(startDate, endDate);
  };

  const setQuick = (days: number) => {
    const today = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const e = fmt(today);
    const s = new Date(today); s.setDate(s.getDate() - (days - 1));
    const sStr = fmt(s);
    setStartDate(sStr);
    setEndDate(e);
    load(sStr, e);
  };

  if (loading && !data) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('usageSummary.loading')}</div>;
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>{t('usageSummary.dateStart')}</label>
        <input
          type="date"
          value={startDate}
          max={endDate || undefined}
          onChange={e => setStartDate(e.target.value)}
          disabled={loading}
          style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text)' }}
        />
        <label style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>{t('usageSummary.dateEnd')}</label>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={e => setEndDate(e.target.value)}
          disabled={loading}
          style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text)' }}
        />
        <button className="btn btn-primary btn-sm" onClick={handleQuery} disabled={loading}>{t('usageSummary.queryBtn')}</button>

        <div style={{ display: 'flex', gap: '0.35rem', marginLeft: '0.5rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setQuick(7)} disabled={loading}>{t('usageSummary.last7Days')}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setQuick(30)} disabled={loading}>{t('usageSummary.last30Days')}</button>
        </div>

        {data && (
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--text-3)' }}>
            {t('usageSummary.rangeSummary', { days: data.days, start: data.start, end: data.end })}
          </span>
        )}
      </div>

      <ReconciliationTables
        videos={data?.video || []}
        images={data?.image || []}
        llms={data?.llm || []}
        services={data?.service || []}
        totalCost={data?.totalCost || 0}
        totalLabel={t('usageSummary.rangeTotal')}
        emptyDescription={data ? t('usageSummary.rangeEmpty', { range: `${data.start} ~ ${data.end}` }) : t('usageSummary.rangeEmptyDefault')}
      />
    </>
  );
};

// ReconciliationTab 平台对账主体(月度/区间)。已拆为独立页 PlatformReconciliation 使用,故导出。
export const ReconciliationTab: React.FC = () => {
  const { t } = useTranslation('profile');
  const toast = useToast();
  const [mode, setMode] = useState<'month' | 'range'>('month');

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.9rem',
    fontSize: '0.85rem',
    borderRadius: '6px',
    border: '1px solid var(--border)',
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text-2)',
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
        <button style={tabBtnStyle(mode === 'month')} onClick={() => setMode('month')}>{t('usageSummary.tabMonth')}</button>
        <button style={tabBtnStyle(mode === 'range')} onClick={() => setMode('range')}>{t('usageSummary.tabRange')}</button>
        {mode === 'range' && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginLeft: '0.5rem' }}>
            {t('usageSummary.rangeHint')}
          </span>
        )}
      </div>

      {mode === 'month' ? (
        <MonthlyReconciliationView toast={toast} />
      ) : (
        <RangeReconciliationView toast={toast} />
      )}
    </>
  );
};

// ==================== Main Component ====================

const UsageSummary: React.FC = () => {
  const { t } = useTranslation('profile');

  // 平台对账已拆到独立页 /platform-reconciliation;本页仅保留"用量总计"。
  return (
    <div className="container">
      <Breadcrumb items={[
        { label: t('usageSummary.breadcrumbProjects'), to: '/projects' },
        { label: t('usageSummary.breadcrumbTitle') },
      ]} />

      <div className="page-header">
        <h1 className="page-title">{t('usageSummary.pageTitle')}</h1>
      </div>

      <OverviewTab />
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

export default UsageSummary;
